/**
 * Combines multiple arrays into a single array with unique elements
 *
 * @param arrays - Arrays to combine
 * @param compareFn - Optional comparison function to determine equality (defaults to strict equality)
 * @returns Combined array with duplicates removed
 */
export function combineArraysWithoutDuplicates<T>(
  arrays: (T[] | undefined | null)[],
  compareFn?: (item1: T, item2: T) => boolean
): T[] {
  // Filter out undefined/null arrays
  const validArrays = arrays.filter((arr): arr is T[] => Array.isArray(arr))

  // If no valid arrays, return empty array
  if (validArrays.length === 0) {
    return []
  }

  // Flatten all arrays into a single array
  const combinedArray = validArrays.reduce<T[]>((acc, arr) => acc.concat(arr), [])

  // If no items, return empty array
  if (combinedArray.length === 0) {
    return []
  }

  // Default comparison function uses strict equality
  const defaultCompareFn = (item1: T, item2: T): boolean => item1 === item2
  const equalityFn = compareFn ?? defaultCompareFn

  // Filter out duplicates while preserving order
  return combinedArray.filter((item, index) => {
    // Keep the item if it's the first occurrence
    return combinedArray.findIndex((existingItem) => equalityFn(item, existingItem)) === index
  })
}
